// src/library/modules/UserModule.ts

import ModuleTemplate from "./moduleTemplate";
import type { ModuleProperty, ModuleOutput, Shape } from "../typeDefinations";
import type { UserComponentJson } from "@/types/userComponent";
import { Rectangle, Circle, Polygon, Path } from "../primitives";
import { useContentStore } from "@/stores/content";
import { parseSVG, makeAbsolute } from "svg-path-parser";
import {
  readUserNumber,
  resolvePortPosition,
  type RenderContext
} from "./userComponentExpressions";

type AnyShape = any;

function computeBBoxFromShapes(shapes: AnyShape[]) {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  for (const s of shapes || []) {
    if (!s) continue;

    if (typeof s.x === "number" && typeof s.y === "number") {
      const x0 = s.x;
      const y0 = s.y;
      const w = typeof s.width === "number" ? s.width : 0;
      const h = typeof s.height === "number" ? s.height : 0;

      const x1 = x0 + w;
      const y1 = y0 + h;

      minX = Math.min(minX, x0, x1);
      minY = Math.min(minY, y0, y1);
      maxX = Math.max(maxX, x0, x1);
      maxY = Math.max(maxY, y0, y1);
      continue;
    }

    if (typeof s.cx === "number" && typeof s.cy === "number") {
      const r = typeof s.r === "number" ? s.r : 0;
      minX = Math.min(minX, s.cx - r);
      maxX = Math.max(maxX, s.cx + r);
      minY = Math.min(minY, s.cy - r);
      maxY = Math.max(maxY, s.cy + r);
      continue;
    }

    if (Array.isArray(s.points)) {
      for (const p of s.points) {
        const px = p?.[0];
        const py = p?.[1];
        if (typeof px === "number" && typeof py === "number") {
          minX = Math.min(minX, px);
          maxX = Math.max(maxX, px);
          minY = Math.min(minY, py);
          maxY = Math.max(maxY, py);
        }
      }
      continue;
    }

    if (typeof s.d === "string") {
      try {
        const pathSegments = makeAbsolute(parseSVG(s.d));
        const offsetX = s.pathOffset?.[0] ?? 0;
        const offsetY = s.pathOffset?.[1] ?? 0;
        for (const seg of pathSegments) {
          if (typeof seg.x === "number" && typeof seg.y === "number") {
            minX = Math.min(minX, seg.x + offsetX);
            maxX = Math.max(maxX, seg.x + offsetX);
            minY = Math.min(minY, seg.y + offsetY);
            maxY = Math.max(maxY, seg.y + offsetY);
          }
        }
      } catch (e) {
        console.warn("Failed to parse path for bbox in UserModule", e);
      }
      continue;
    }
  }

  const ok =
    Number.isFinite(minX) &&
    Number.isFinite(minY) &&
    Number.isFinite(maxX) &&
    Number.isFinite(maxY);

  return ok ? { minX, minY, maxX, maxY } : null;
}

function ensureHeight(props: any, fallback = 200) {
  if (!props) props = {};
  const h = props.height?.value;
  if (typeof h !== "number" || Number.isNaN(h)) {
    props.height = { value: fallback, name: "height", unit: "", step: 1 };
  }
  return props;
}

function resolveDimension(
  value: unknown,
  properties: ModuleProperty,
  fallback: number
): number {
  const resolved = readUserNumber(value, properties, fallback);
  return Number.isFinite(resolved) && resolved !== 0 ? resolved : fallback;
}

function safeScale(current: number, initial: number): number {
  const scale = current / initial;
  return Number.isFinite(scale) && scale !== 0 ? scale : 1;
}

export class UserModule extends ModuleTemplate {
  id = "";
  properties: ModuleProperty;

  private static _counter = 0;
  private component: UserComponentJson;

 
  constructor(
    component: UserComponentJson,
    pos: number[] | [number, number],
    layer: string,
    rotation = 0,
    forcedId?: string,
    initialProperties?: Record<string, number>
  ) {
    super(pos as any, layer, rotation);
    this.component = component;

    (this as any).componentId = (component as any).id ?? component.name;

    const cloned = JSON.parse(JSON.stringify(component.properties ?? {}));
    this.properties = ensureHeight(cloned, 200) as ModuleProperty;

    for (const [key, p] of Object.entries(this.properties)) {
      if (!p) continue;
      if ((p as any).unit == null) (p as any).unit = "";
      if ((p as any).step == null) (p as any).step = 1;
      if ((p as any).name == null) (p as any).name = key;
    }

    if (initialProperties) {
      for (const [key, value] of Object.entries(initialProperties)) {
        if (key in this.properties && Number.isFinite(value)) {
          (this.properties as any)[key].value = value;
        }
      }
    }

    
    if (forcedId && typeof forcedId === "string") {
      this.id = forcedId;


      const m = forcedId.match(/UserModule-(\d+)/);
      if (m) {
        const n = Number(m[1]);
        if (Number.isFinite(n)) {
          UserModule._counter = Math.max(UserModule._counter, n);
        }
      }
    } else {
      this.setNewModuleId();
    }

    this.initializeChannelPorts();
    this.updateRender();
  }

  get numberOfChannelPorts(): number {
    return this.component.ports?.length ?? 0;
  }

  get portSizeFixed(): boolean {
    return false;
  }

  get getModuleIndex(): number {
    const idx = (this.component as any)?.extras?.moduleIdx;
    if (typeof idx !== "number" || Number.isNaN(idx)) {
      console.warn(
        `[UserModule] component.extras.moduleIdx missing; fallback to 1000. component=${this.component?.name}`
      );
      return 1000;
    }
    return idx;
  }

  setNewModuleId(): void {
    UserModule._counter++;
    const contentStore = useContentStore();
    while (contentStore.contentMap.has("UserModule-" + UserModule._counter)) {
      UserModule._counter++;
    }
    this.id = "UserModule-" + UserModule._counter;
  }

  updateRender(): void {
    const cx =
      Array.isArray(this.position) && typeof this.position[0] === "number"
        ? this.position[0]
        : 0;
    const cy =
      Array.isArray(this.position) && typeof this.position[1] === "number"
        ? this.position[1]
        : 0;

    this.elementsToRender.centerPoint = [cx, cy];

    const currentLength = resolveDimension(
      (this.properties as any).length?.value,
      this.properties,
      4000
    );
    const currentWidth = resolveDimension(
      (this.properties as any).width?.value,
      this.properties,
      3200
    );

    const defaultLength = resolveDimension(
      (this.component.properties as any).length?.value,
      this.properties,
      currentLength
    );
    const defaultWidth = resolveDimension(
      (this.component.properties as any).width?.value,
      this.properties,
      currentWidth
    );

    const scaleX = safeScale(currentLength, defaultLength);
    const scaleY = safeScale(currentWidth, defaultWidth);
    const minScale = Math.min(scaleX, scaleY);

    const ctx: RenderContext = {
      center: [cx, cy],
      properties: this.properties,
      componentProperties: this.component.properties,
      scaleX,
      scaleY,
      minScale
    };
    const getOffset = (obj: any, key1: string, key2: string, scale: number) => {
      const vars = obj.__vars ?? {};
      if (obj[key1 + 'Expr']) return readUserNumber(obj[key1 + 'Expr'], this.properties, 0, vars) * scale;
      if (obj[key2 + 'Expr']) return readUserNumber(obj[key2 + 'Expr'], this.properties, 0, vars) * scale;
      const shift = key1 === "x" || key1 === "cx" ? obj.__shiftX ?? 0 : obj.__shiftY ?? 0;
      return (readUserNumber(obj[key1] ?? obj[key2] ?? 0, this.properties, 0, vars) + shift) * scale;
    };
    const expandGeometry = (
      descs: any[] = [],
      shiftX = 0,
      shiftY = 0,
      vars: Record<string, number> = {}
    ): any[] => {
      const expanded: any[] = [];
      descs.forEach((desc) => {
        if (!desc || !desc.kind) return;
        const nextShiftX = shiftX + readUserNumber(desc.x ?? desc.cx ?? 0, this.properties, 0, vars);
        const nextShiftY = shiftY + readUserNumber(desc.y ?? desc.cy ?? 0, this.properties, 0, vars);

        if (desc.kind === "composite") {
          expanded.push(...expandGeometry(desc.children || [], nextShiftX, nextShiftY, vars));
          return;
        }

        if (desc.kind === "repeat") {
          // A repeat count of zero is meaningful for conditional geometry (for
          // example, DLD edge pillars which only exist for some shifted rows).
          const times = Math.max(0, Math.floor(readUserNumber(desc.times, this.properties, 1, vars)));
          const dx = readUserNumber(desc.dx ?? 0, this.properties, 0, vars);
          const dy = readUserNumber(desc.dy ?? 0, this.properties, 0, vars);
          for (let i = 0; i < times; i++) {
            const nextVars = desc.index ? { ...vars, [desc.index]: i } : vars;
            expanded.push(...expandGeometry(desc.children || [], nextShiftX + dx * i, nextShiftY + dy * i, nextVars));
          }
          return;
        }

        expanded.push({
          ...desc,
          __shiftX: (desc.__shiftX ?? 0) + shiftX,
          __shiftY: (desc.__shiftY ?? 0) + shiftY,
          __vars: vars
        });
      });
      return expanded;
    };

    const borderShapes: Shape[] = [];
    const maskShapes: Shape[] = [];
    const geo: any = (this.component as any).geometry;

    if (geo && Array.isArray(geo.border)) {
      expandGeometry(geo.border).forEach((b: any) => {
        if (!b || !b.kind) return;
        const borderFilled = false;

        if (b.kind === "rect") {
          borderShapes.push(
            new Rectangle(
              cx + getOffset(b, 'x', 'cx', scaleX),
              cy + getOffset(b, 'y', 'cy', scaleY),
              readUserNumber(b.cornerRadius ?? 0, this.properties, 0, b.__vars ?? {}) * minScale,
              readUserNumber(b.width ?? 0, this.properties, 0, b.__vars ?? {}) * scaleX,
              readUserNumber(b.height ?? 0, this.properties, 0, b.__vars ?? {}) * scaleY,
              borderFilled
            ) as any
          );
        } else if (b.kind === "circle") {
          borderShapes.push(
            new Circle(
              cx + getOffset(b, 'cx', 'x', scaleX),
              cy + getOffset(b, 'cy', 'y', scaleY),
              readUserNumber(b.r ?? 0, this.properties, 0, b.__vars ?? {}) * minScale,
              borderFilled
            ) as any
          );
        } else if (b.kind === "polygon") {
          const offsetX = getOffset(b, 'x', 'cx', scaleX);
          const offsetY = getOffset(b, 'y', 'cy', scaleY);
          const vars = b.__vars ?? {};
          const absPts = (b.points || []).map((p: any) => [
            cx + offsetX + readUserNumber(p?.[0] ?? 0, this.properties, 0, vars) * scaleX,
            cy + offsetY + readUserNumber(p?.[1] ?? 0, this.properties, 0, vars) * scaleY
          ]);
          borderShapes.push(
            new Polygon(absPts, borderFilled, readUserNumber(b.cornerRadius ?? 0, this.properties, 0, vars) * minScale) as any
          );
        } else if (b.kind === "path") {
          const pathOffset = [
            cx + getOffset(b, 'x', 'cx', scaleX),
            cy + getOffset(b, 'y', 'cy', scaleY)
          ];
          borderShapes.push(new Path(b.d ?? "", readUserNumber(b.strokeWidth ?? 1, this.properties, 1, b.__vars ?? {}) * minScale, borderFilled, pathOffset) as any);
        }
      });
    }

    if (geo && Array.isArray(geo.mask)) {
      expandGeometry(geo.mask).forEach((m: any) => {
        if (!m || !m.kind) return;
        let shape: any = null;
        const maskFilled = true;

        if (m.kind === "circle") {
          // A mask circle must use its own JSON radius expression. Falling
          // back to a component property named `radius` makes ring-shaped
          // components cut away their entire outer circle.
          const r = readUserNumber(
            m.r ?? 0,
            this.properties,
            0,
            m.__vars ?? {}
          );

          const mcx = cx + getOffset(m, 'cx', 'x', scaleX);
          const mcy = cy + getOffset(m, 'cy', 'y', scaleY);

          shape = new Circle(mcx, mcy, r, maskFilled);
        } else if (m.kind === "rect") {
          shape = new Rectangle(
            cx + getOffset(m, 'x', 'cx', scaleX),
            cy + getOffset(m, 'y', 'cy', scaleY),
            readUserNumber(m.cornerRadius ?? 0, this.properties, 0, m.__vars ?? {}) * minScale,
            readUserNumber(m.width ?? 0, this.properties, 0, m.__vars ?? {}) * scaleX,
            readUserNumber(m.height ?? 0, this.properties, 0, m.__vars ?? {}) * scaleY,
            maskFilled
          );
        } else if (m.kind === "polygon") {
          const offsetX = getOffset(m, 'x', 'cx', scaleX);
          const offsetY = getOffset(m, 'y', 'cy', scaleY);
          const vars = m.__vars ?? {};
          const absPts = (m.points || []).map((p: any) => [
            cx + offsetX + readUserNumber(p?.[0] ?? 0, this.properties, 0, vars) * scaleX,
            cy + offsetY + readUserNumber(p?.[1] ?? 0, this.properties, 0, vars) * scaleY
          ]);
          shape = new Polygon(absPts, maskFilled, readUserNumber(m.cornerRadius ?? 0, this.properties, 0, vars) * minScale);
        } else if (m.kind === "path") {
          const pathOffset = [
            cx + getOffset(m, 'x', 'cx', scaleX),
            cy + getOffset(m, 'y', 'cy', scaleY)
          ];
          shape = new Path(m.d ?? "", readUserNumber(m.strokeWidth ?? 1, this.properties, 1, m.__vars ?? {}) * minScale, maskFilled, pathOffset);
        }

        if (shape) maskShapes.push(shape);
      });
    }

    if (borderShapes.length === 0) {
      borderShapes.push(
        new Rectangle(cx - currentLength / 2, cy - currentWidth / 2, 0, currentLength, currentWidth, true) as any
      );
    }

    this.elementsToRender.borderShapes = borderShapes as any;
    this.elementsToRender.maskShapes = maskShapes as any;

    const bbox = computeBBoxFromShapes([...(borderShapes as any[]), ...(maskShapes as any[])]);

    if (bbox) {
      const pad = 0;
      this.elementsToRender.topLeftCorner = [bbox.minX - pad, bbox.minY - pad];
    } else {
      this.elementsToRender.topLeftCorner = [cx - currentLength / 2, cy - currentWidth / 2];
    }

    this.updatePort();
  }

  updatePortHelper(): void {
    if (!this.component.ports) return;

    const cx =
      Array.isArray(this.position) && typeof this.position[0] === "number"
        ? this.position[0]
        : 0;
    const cy =
      Array.isArray(this.position) && typeof this.position[1] === "number"
        ? this.position[1]
        : 0;
    const currentLength = resolveDimension(
      (this.properties as any).length?.value,
      this.properties,
      4000
    );
    const currentWidth = resolveDimension(
      (this.properties as any).width?.value,
      this.properties,
      3200
    );
    const defaultLength = resolveDimension(
      (this.component.properties as any).length?.value,
      this.properties,
      currentLength
    );
    const defaultWidth = resolveDimension(
      (this.component.properties as any).width?.value,
      this.properties,
      currentWidth
    );
    const scaleX = safeScale(currentLength, defaultLength);
    const scaleY = safeScale(currentWidth, defaultWidth);
    const ctx: RenderContext = {
      center: [cx, cy],
      properties: this.properties,
      componentProperties: this.component.properties,
      scaleX,
      scaleY,
      minScale: Math.min(scaleX, scaleY)
    };

    this.component.ports.forEach((pDesc, idx) => {
      const port = this.channelPorts[idx];
      if (!port) return;
      const position = resolvePortPosition(pDesc, ctx);
      port.position[0] = position[0];
      port.position[1] = position[1];
    });

    if (this.rotation !== 0) {
      const r = this.rotation;
      this.rotation = 0;
      this.setRotation(r);
    }
  }

  clone(): UserModule {
    const propertyValues = Object.fromEntries(
      Object.entries(this.properties).map(([key, prop]) => [key, (prop as any).value])
    );
    return new UserModule(
      this.component,
      [...this.position],
      this.layer,
      this.rotation,
      undefined,
      propertyValues
    );
  }

  static parse(component: UserComponentJson, content: ModuleOutput): UserModule {
  
    return new UserModule(
      component,
      content.position,
      content.layer,
      content.rotation,
      content.id,
      content.properties
    );
  }
}







